import React, { useEffect, useRef } from 'react';
import { V1SectionContainer } from './V1SectionContainer';
import { useEventContext } from '../../../contexts/EventContext';
import { useEventDetails } from '../../../../hooks/useEventDetails';

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  details?: string;
  sectionOption?: any;
}

interface V1DetailsSectionProps {
  section: Section;
}

/**
 * V1 Details Section - FROZEN
 *
 * Displays HTML content with optional title.
 * Supports TinyMCE-generated content with proper styling.
 *
 * DO NOT add V2 features to this component.
 */
export const V1DetailsSection: React.FC<V1DetailsSectionProps> = ({ section }) => {
  // The backend already handles the logic for showing registered details
  // The section.details field will contain the appropriate content based on user registration status
  const details = section.details;
  const contentRef = useRef<HTMLDivElement>(null);

  // Get event primary color
  const { eventSlug, style: contextStyle } = useEventContext();
  const { data: eventDetails } = useEventDetails(eventSlug);
  const eventStyle = contextStyle || eventDetails?.style;
  const primaryColor = eventStyle?.headerColor || '#007bff';

  // Apply fallback classes for floated images in links
  useEffect(() => {
    if (contentRef.current) {
      // Find all anchors containing floated images
      const anchorsWithFloatLeft = contentRef.current.querySelectorAll('a > img.media-float-left');
      anchorsWithFloatLeft.forEach(img => {
        const anchor = img.parentElement as HTMLAnchorElement;
        if (anchor) {
          anchor.classList.add('has-float-left');
        }
      });

      const anchorsWithFloatRight = contentRef.current.querySelectorAll('a > img.media-float-right');
      anchorsWithFloatRight.forEach(img => {
        const anchor = img.parentElement as HTMLAnchorElement;
        if (anchor) {
          anchor.classList.add('has-float-right');
        }
      });
    }
  }, [details]);

  // Debug logging
  console.log('V1DetailsSection - section:', section);
  console.log('V1DetailsSection - details content:', details);

  return (
    <>
      <style>
        {`
          /* Preserve all inline styles from TinyMCE */
          .v1-details-content[style] {
            all: revert;
          }

          /* Allow inline styles on all elements */
          .v1-details-content * {
            /* Don't override color if set inline */
            color: inherit;
          }

          /* Respect inline styles for text formatting */
          .v1-details-content span[style],
          .v1-details-content p[style],
          .v1-details-content div[style],
          .v1-details-content h1[style],
          .v1-details-content h2[style],
          .v1-details-content h3[style],
          .v1-details-content h4[style],
          .v1-details-content h5[style],
          .v1-details-content h6[style] {
            /* Ensure inline styles take precedence */
            all: revert;
          }

          /* Base typography - only apply if no inline styles */
          .v1-details-content {
            color: #333;
            font-size: 15px;
            line-height: 1.6;
          }

          .v1-details-content h1 { font-size: 2.5em; margin: 0.67em 0; }
          .v1-details-content h2 { font-size: 2em; margin: 0.75em 0; }
          .v1-details-content h3 { font-size: 1.5em; margin: 0.83em 0; }
          .v1-details-content h4 { font-size: 1.2em; margin: 1.12em 0; }
          .v1-details-content h5 { font-size: 1em; margin: 1.5em 0; }
          .v1-details-content h6 { font-size: 0.85em; margin: 1.67em 0; }

          .v1-details-content p { margin: 1em 0; }
          .v1-details-content a { color: #358eff; }

          /* Lists - Override Tailwind's reset */
          .v1-details-content ul {
            list-style-type: disc !important;
            margin: 1em 0;
            padding-left: 2em;
            color: #333;
          }

          .v1-details-content ol {
            list-style-type: decimal !important;
            margin: 1em 0;
            padding-left: 2em;
            color: #333;
          }

          .v1-details-content ul ul {
            list-style-type: circle !important;
          }

          .v1-details-content ul ul ul {
            list-style-type: square !important;
          }

          .v1-details-content li {
            display: list-item !important;
            margin-bottom: 0.25em;
          }

          /* Media positioning classes */
          .v1-details-content .media-center {
            margin-left: auto;
            margin-right: auto;
            display: block;
          }
          .v1-details-content .media-float-left {
            float: left;
            margin-right: 20px;
            margin-bottom: 10px;
          }
          .v1-details-content .media-float-right {
            float: right;
            margin-left: 20px;
            margin-bottom: 10px;
          }
          .v1-details-content img {
            max-width: 100%;
            height: auto;
            display: block;
          }

          /* Allow images in centered paragraphs to be inline for centering */
          .v1-details-content p[style*="text-align: center"] img,
          .v1-details-content p[style*="text-align:center"] img {
            display: inline-block !important;
            margin-left: auto;
            margin-right: auto;
          }

          /* Handle linked images - prevent anchor tags from stretching full width */
          .v1-details-content a img {
            display: block;
          }

          /* When images are wrapped in links, apply the float to the link instead */
          .v1-details-content a {
            display: inline-block;
            line-height: 0;
          }

          /* Override for text links (not image links) */
          .v1-details-content a:not(:has(img)) {
            display: inline;
            line-height: inherit;
          }

          /* For browsers that don't support :has, use a fallback */
          .v1-details-content p a,
          .v1-details-content div a {
            display: inline-block;
            vertical-align: top;
          }

          /* Ensure floated images within links work properly */
          .v1-details-content a img.media-float-left {
            float: none; /* Remove float from image */
            margin: 0; /* Reset margin */
          }

          .v1-details-content a img.media-float-right {
            float: none; /* Remove float from image */
            margin: 0; /* Reset margin */
          }

          /* Apply float to the parent anchor instead */
          .v1-details-content a:has(img.media-float-left) {
            float: left;
            margin-right: 20px;
            margin-bottom: 10px;
          }

          .v1-details-content a:has(img.media-float-right) {
            float: right;
            margin-left: 20px;
            margin-bottom: 10px;
          }

          /* Fallback for browsers without :has support - use JavaScript class */
          .v1-details-content a.has-float-left {
            float: left;
            margin-right: 20px;
            margin-bottom: 10px;
          }

          .v1-details-content a.has-float-right {
            float: right;
            margin-left: 20px;
            margin-bottom: 10px;
          }

          /* Button styles */
          .v1-details-content .btn {
            display: inline-block;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 600;
            text-align: center;
            text-decoration: none;
            border-radius: 4px;
            transition: all 0.2s ease;
            cursor: pointer;
            border: 1px solid transparent;
          }
          .v1-details-content .cs-new-blue-btn {
            background-color: ${primaryColor};
            color: white !important;
            border-color: ${primaryColor};
          }
          .v1-details-content .cs-new-blue-btn:hover {
            background-color: ${primaryColor};
            border-color: ${primaryColor};
            color: white !important;
            text-decoration: none;
            opacity: 0.85;
          }
          .v1-details-content .cs-btn-gray-new {
            background-color: #6c757d;
            color: white !important;
            border-color: #6c757d;
          }
          .v1-details-content .cs-btn-gray-new:hover {
            background-color: #545b62;
            border-color: #545b62;
            color: white !important;
            text-decoration: none;
          }

          /* Tables */
          .v1-details-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 1em 0;
          }
          .v1-details-content td, .v1-details-content th {
            border: 1px solid #ddd;
            padding: 8px;
          }

          /* Clear floats */
          .v1-details-content::after {
            content: "";
            display: table;
            clear: both;
          }
        `}
      </style>
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-4">{section.customTitle}</h2>
        )}
        <div
          ref={contentRef}
          className="v1-details-content"
          dangerouslySetInnerHTML={{ __html: details || '' }}
        />
      </V1SectionContainer>
    </>
  );
};